import { Injectable, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { filter, map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EventMessageType } from '../models/event-message-type.enum';
import { EventMessage } from '../models/event-message.model';
import { CurrentUserService } from './current-user.service';
import { ADMIN_APP_ID } from '../models/admin.constants';
import { EnvironmentService } from './environment.service';
import { EventSourcePolyfill } from 'event-source-polyfill';


@Injectable({
  providedIn: 'root',
})
export class SseEventsService {
  private readonly currentUserService = inject(CurrentUserService);
  private readonly environmentService = inject(EnvironmentService);

  private readonly serviceUrl: string = environment.baseUrl + '/events';
  private eventSource: EventSourcePolyfill | null = null;
  private readonly _events$: BehaviorSubject<EventMessage[]> = new BehaviorSubject<
    EventMessage[]
  >([]);

  private readonly _event$: Subject<EventMessage> = new Subject<EventMessage>();
  private readonly currentEnvironment$ = this.environmentService.currentEnvironment$;

  // ------------------------------------------------- INIT ---------------------------------------------------- //
  private initEventSource(): void {
    this.stopListening();
    this.eventSource = new EventSourcePolyfill(this.serviceUrl, {
      headers: {
        'Request-Origin': ADMIN_APP_ID,
        'Request-Environment-Id': this.currentEnvironment$()?.id?.toString() ?? '',
        Authorization: 'Bearer ' + this.currentUserService.currentUser$()?.token,
      },
      heartbeatTimeout: 1000000000,
    });
  }

  // -------------------------------------------------- SSE CONTROLS ------------------------------------------------------ //

  public startListening(): void {
    this.initEventSource();

    this.eventSource!.onmessage = (event: any) => {
      const newEvent: EventMessage = JSON.parse(event.data);

      const newEvents = this._events$.value
        .filter((e) => e.type !== newEvent.type);
      newEvents.push(newEvent);

      this._events$.next(newEvents);
      this._event$.next(newEvent);
    };

    this.eventSource!.onerror = (event: any) => {
      console.error(event);
    };
  }

  public stopListening(): void {
    if (this.eventSource) {
      this._events$.next([]);
      this.eventSource.close();
    }
  }

  // ------------------------------------------------- EVENT ACCESSOR ---------------------------------------------------- //

  public on(type: EventMessageType): Observable<EventMessage | undefined> {
    return this._events$.asObservable()
      .pipe(
        map((events) => {
          return events.find((event) => {
            return type === event.type;
          });
        })
      );
  }

  public onSubject(type: EventMessageType, dataType?: string): Observable<EventMessage> {
    return this.onSubjects([type], dataType);
  }

  public onSubjects(types: EventMessageType[], dataType?: string): Observable<EventMessage> {
    return this._event$
      .asObservable()
      .pipe(
        filter((event: EventMessage) => types.includes(event.type) && (dataType ? dataType === event.dataType : true))
      );
  }
}
